import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Lock, Crown, Camera, Eye, EyeOff, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import {
    getDefaultBackground,
    uploadDefaultBackground,
    removeDefaultBackground,
    getCreatorVisibility,
    setCreatorVisibility,
} from "../lib/api";
import { tap } from "../lib/haptic";
import { track } from "../lib/track";

/**
 * SignatureBackdrop — Premium creator's default photo for every card.
 *
 * Mounts on the Dashboard. Premium users upload one photo once → it
 * auto-applies to every NEW card they create afterwards. Editing or
 * removing doesn't touch previously-created cards (they keep their
 * own copy of the photo URL).
 *
 * Free users see a locked tile with a copy-driven upsell CTA. The
 * locked tile is deliberately motivating, not just a wall.
 */
export default function SignatureBackdrop({ isPremium, onUpsell }) {
    const [url, setUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [profilePublic, setProfilePublic] = useState(false);
    const [togglingVisibility, setTogglingVisibility] = useState(false);
    // Motion preview — cycles through drift/breathe/shimmer/glow so creators
    // can audition what their backdrop looks like animated before relying
    // on it. Local-only — doesn't persist anywhere.
    const [previewMotion, setPreviewMotion] = useState(null); // null | "drift" | "breathe" | "shimmer" | "glow"
    const MOTION_CYCLE = ["drift", "breathe", "shimmer", "glow"];
    const fileInputRef = useRef(null);

    const loadBackdrop = async () => {
        try {
            setError(false);
            const [bgData, visData] = await Promise.all([
                getDefaultBackground(),
                getCreatorVisibility().catch(() => ({ profile_public: false })),
            ]);
            setUrl(bgData?.url || null);
            setProfilePublic(!!visData?.profile_public);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isPremium) {
            setLoading(false);
            return;
        }
        loadBackdrop();
    }, [isPremium]);  // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast("Image must be 5MB or smaller.");
            return;
        }
        setUploading(true);
        try {
            tap();
            const data = await uploadDefaultBackground(file);
            setUrl(data.url);
            track("signature_backdrop_uploaded", { size: file.size });
            toast("Signature backdrop saved — every new card carries your photo.", { duration: 3200 });
        } catch (err) {
            const code = err?.response?.status;
            if (code === 402 && onUpsell) onUpsell();
            else if (code === 413) toast("Image must be 5MB or smaller.");
            else if (code === 400) toast("Try a JPG, PNG, or WebP image.");
            else toast("Upload failed — please try again.");
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!url) return;
        tap();
        try {
            await removeDefaultBackground();
            setUrl(null);
            track("signature_backdrop_removed");
            toast("Signature backdrop cleared.");
        } catch {
            toast("Could not remove — try again.");
        }
    };

    // ── Free-user (locked) tile ──
    if (!isPremium) {
        return (
            <section
                className="rounded-2xl border border-app/20 bg-surface-1 p-5"
                data-testid="signature-backdrop-locked"
            >
                <div className="flex items-center gap-2 mb-3">
                    <Camera className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                        signature backdrop
                    </span>
                    <Lock className="w-3 h-3 text-amber-200/70 ml-auto" strokeWidth={1.6} />
                </div>
                <p className="font-serif italic text-app text-[15px] leading-snug mb-2">
                    set one photo. every card you make wears it.
                </p>
                <p className="font-sans text-[11px] text-app-3 mb-4">
                    Premium feature for creators — your couple shot, your gallery, your brand mark, automatically behind every new mood card.
                </p>
                <button
                    type="button"
                    onClick={() => {
                        track("signature_backdrop_locked_clicked");
                        if (onUpsell) onUpsell();
                    }}
                    data-testid="signature-backdrop-upsell-button"
                    className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300/40 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors"
                >
                    <Crown className="w-3 h-3" strokeWidth={1.5} />
                    <span>see premium plans</span>
                </button>
            </section>
        );
    }

    // ── Premium tile ──
    return (
        <section
            className="rounded-2xl border border-app/20 bg-surface-1 p-5"
            data-testid="signature-backdrop-premium"
        >
            <div className="flex items-center gap-2 mb-3">
                <Camera className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                    signature backdrop
                </span>
                {url && (
                    <span className="ml-auto flex items-center gap-1 font-sans text-[9px] uppercase tracking-[0.3em] text-amber-200/85">
                        <span className="h-1 w-1 rounded-full bg-amber-300/85" />
                        active
                    </span>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="signature-backdrop-input"
                onChange={handleUpload}
            />

            {loading ? (
                <p className="font-sans text-[11px] text-app-3">loading…</p>
            ) : error ? (
                <div data-testid="signature-backdrop-error">
                    <p className="font-sans text-[11px] text-app-3 mb-2">
                        couldn't load — your connection might be flaky.
                    </p>
                    <button
                        type="button"
                        onClick={() => { setLoading(true); loadBackdrop(); }}
                        data-testid="signature-backdrop-retry-button"
                        className="btn-press inline-flex items-center gap-1 font-sans text-[10px] uppercase tracking-[0.32em] text-amber-200/85 hover:text-amber-100"
                    >
                        retry
                    </button>
                </div>
            ) : url ? (
                <>
                    <div className="rounded-xl overflow-hidden border border-app/20 mb-3 relative" style={{ aspectRatio: "3 / 4", maxHeight: "180px" }}>
                        <div
                            data-testid="signature-backdrop-preview"
                            className={`w-full h-full ${previewMotion ? `motion-${previewMotion}` : ""}`}
                            style={{
                                background: `url("${url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${url}` : url}") center/cover no-repeat`,
                            }}
                        />
                        {/* Motion-preview toggle — Premium discoverability hint. */}
                        <button
                            type="button"
                            onClick={() => {
                                tap();
                                setPreviewMotion((curr) => {
                                    if (!curr) return MOTION_CYCLE[0];
                                    const i = MOTION_CYCLE.indexOf(curr);
                                    if (i === MOTION_CYCLE.length - 1) return null;
                                    return MOTION_CYCLE[i + 1];
                                });
                                track("signature_motion_preview_toggled");
                            }}
                            data-testid="signature-backdrop-motion-preview-button"
                            className="absolute bottom-2 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm text-white/90 hover:text-white text-[9px] uppercase tracking-[0.25em] transition-colors btn-press"
                        >
                            {previewMotion
                                ? <Pause className="w-2.5 h-2.5" strokeWidth={1.8} />
                                : <Play className="w-2.5 h-2.5" strokeWidth={1.8} />}
                            <span>{previewMotion || "preview motion"}</span>
                        </button>
                    </div>
                    <p className="font-sans text-[11px] text-app-3 mb-3">
                        every new card you make starts with this photo. older cards aren't affected.
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="signature-backdrop-replace-button"
                            className="btn-press flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border border-app/20 bg-surface-1 text-app-2 hover:text-app hover:border-app/45 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors"
                        >
                            <ImagePlus className="w-3 h-3" strokeWidth={1.6} />
                            <span>{uploading ? "uploading…" : "replace photo"}</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleRemove}
                            disabled={uploading}
                            aria-label="Remove signature backdrop"
                            data-testid="signature-backdrop-remove-button"
                            className="btn-press flex items-center justify-center w-10 h-10 rounded-xl border border-app/20 bg-surface-1 text-app-3 hover:text-rose-400 hover:border-rose-400/40 transition-colors"
                        >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                        </button>
                    </div>

                    {/* Creators gallery visibility toggle — only meaningful
                        when a backdrop is set. Off by default. */}
                    <div
                        className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-app/10 bg-app/[0.02] p-3"
                        data-testid="signature-backdrop-visibility-row"
                    >
                        <div className="flex items-start gap-2 min-w-0">
                            {profilePublic
                                ? <Eye className="w-3.5 h-3.5 text-amber-200/85 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                                : <EyeOff className="w-3.5 h-3.5 text-app-3 flex-shrink-0 mt-0.5" strokeWidth={1.5} />}
                            <div className="min-w-0">
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-2 mb-0.5">
                                    feature in creators gallery
                                </p>
                                <p className="font-sans text-[10px] text-app-3 leading-snug">
                                    show your name + backdrop at <span className="font-mono">/creators</span>.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            disabled={togglingVisibility}
                            onClick={async () => {
                                tap();
                                setTogglingVisibility(true);
                                const next = !profilePublic;
                                try {
                                    await setCreatorVisibility(next);
                                    setProfilePublic(next);
                                    track("creator_visibility_toggled", { profile_public: next });
                                    toast(next ? "you're in the creators gallery." : "removed from the gallery.", { duration: 2400 });
                                } catch (err) {
                                    if (err?.response?.status === 402 && onUpsell) onUpsell();
                                    else toast("Could not update — try again.");
                                } finally {
                                    setTogglingVisibility(false);
                                }
                            }}
                            data-testid="signature-backdrop-visibility-toggle"
                            aria-pressed={profilePublic}
                            className={`btn-press relative w-10 h-6 rounded-full border transition-colors flex-shrink-0 ${
                                profilePublic
                                    ? "bg-amber-400/85 border-amber-300/60"
                                    : "bg-surface-1 border-app/30"
                            }`}
                        >
                            <span
                                className={`absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform ${
                                    profilePublic ? "translate-x-[18px]" : "translate-x-[2px]"
                                }`}
                            />
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <p className="font-serif italic text-app text-[15px] leading-snug mb-2">
                        one photo. behind every card you make next.
                    </p>
                    <p className="font-sans text-[11px] text-app-3 mb-4">
                        upload once — your portrait, your couple shot, your studio backdrop — and it follows every new card automatically.
                    </p>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="signature-backdrop-upload-button"
                        className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300/40 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors"
                    >
                        <ImagePlus className="w-3 h-3" strokeWidth={1.5} />
                        <span>{uploading ? "uploading…" : "upload photo"}</span>
                    </button>
                </>
            )}
        </section>
    );
}
